import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

class ApprovalDetailsApiService {
  constructor() {
    this.apiClient = axios.create({
      baseURL: `${API_BASE_URL}/accounts`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to include auth token if needed
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get approval details by encrypted ID and type
   * @param {string} encryptedId - Encrypted application ID
   * @param {string} encryptedType - Encrypted assessment type
   * @returns {Promise<Object>} - Approval details data
   */
  async getApprovalDetails(encryptedId, encryptedType) {
    try {
      const response = await this.apiClient.get('/approval-details', {
        params: {
          id: encryptedId,
          type: encryptedType
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching approval details:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Submit individual approval
   * @param {Object} approvalData - Approval submission data
   * @returns {Promise<Object>} - Submit response
   */
  async submitIndividualApproval(approvalData) {
    try {
      const response = await this.apiClient.post('/submit-individual-approval', approvalData);
      return response.data;
    } catch (error) {
      console.error('Error submitting individual approval:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Send approval to CEO
   * @param {Object} ceoData - CEO submission data
   * @returns {Promise<Object>} - Submit response
   */
  async sendToCEO(ceoData) {
    try {
      const response = await this.apiClient.post('/send-to-ceo', ceoData);
      return response.data;
    } catch (error) {
      console.error('Error sending to CEO:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Process reallotment
   * @param {Object} reallotmentData - Reallotment data
   * @returns {Promise<Object>} - Process response
   */
  async processReallotment(reallotmentData) {
    try {
      const response = await this.apiClient.post('/reallotment-process', reallotmentData);
      return response.data;
    } catch (error) {
      console.error('Error processing reallotment:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   * @param {Error} error - The error to handle
   * @returns {Object} - Formatted error object
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      return {
        success: false,
        message: error.response.data?.message || 'Server error occurred',
        status: error.response.status,
        details: error.response.data?.details
      };
    } else if (error.request) {
      // Request made but no response
      return {
        success: false,
        message: 'Network error - please check your connection',
        status: 0
      };
    } else {
      // Something else happened
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
        status: 0
      };
    }
  }

  /**
   * Send to Lab for Payment
   * @param {Object} data - Payment data
   * @returns {Promise<Object>} - API response
   */
  async sendToLabForPayment(data) {
    try {
      const response = await this.apiClient.post('/send-to-lab-payment', data);
      return response.data;
    } catch (error) {
      console.error('Send to lab for payment failed:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Health check endpoint
   * @returns {Promise<Object>} - Health status
   */
  async healthCheck() {
    try {
      const response = await this.apiClient.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw this.handleError(error);
    }
  }
}

// Create and export a singleton instance
const approvalDetailsApiService = new ApprovalDetailsApiService();
export default approvalDetailsApiService;
